import React, { useState, useEffect } from "react";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import Table from "examples/Tables/Table";
import ArgonButton from "components/ArgonButton";
import SubjectTableData from "Views/Curriculum/data/subjectTableData";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import { baseURL } from "utils/api";
import axios from "utils/api";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Close } from "@mui/icons-material";

function index() {
  const [refresh, setRefresh] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Add/Edit dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [editId, setEditId] = useState(null);
  const [subjectName, setSubjectName] = useState("");
  const [selectedBoard, setSelectedBoard] = useState("");
  const [boards, setBoards] = useState([]);
  const [formLoading, setFormLoading] = useState(false);

  // Fetch boards on component mount
  useEffect(() => {
    fetchBoards();
  }, []);

  const fetchBoards = async () => {
    try {
      const response = await axios.get(`${baseURL}/boards`);
      console.log("Board API Response:", response.data);
      if (response.data.success) {
        const activeBoards = response.data.data.filter((board) => !board.isDeleted);
        console.log("Active Boards:", activeBoards);
        setBoards(activeBoards);
        if (activeBoards.length === 0) {
          toast.info("No boards available. Please create a board first.");
        }
      }
    } catch (error) {
      console.error("Error fetching boards:", error);
      toast.error(
        error.response?.data?.error || error.response?.data?.message || "Failed to load boards"
      );
    }
  };

  // Delete button click handler
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDeleteDialog(false);
    setDeleteId(null); // reset selected id
  };

  const handleAddClick = () => {
    setFormMode("add");
    setEditId(null);
    setSubjectName("");
    setSelectedBoard("");
    setFormOpen(true);
  };

  const handleEditClick = (id, name) => {
    setFormMode("edit");
    setEditId(id);
    setSubjectName(name || "");
    // Fetch subject details to get board
    axios
      .get(`${baseURL}/subjects/${id}`)
      .then((res) => {
        if (res.data.success) {
          setSelectedBoard(res.data.data.board?._id || res.data.data.board || "");
        }
      })
      .catch((err) => {
        console.error("Error fetching subject details:", err);
      });
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setFormLoading(false);
    setSubjectName("");
    setSelectedBoard("");
    setEditId(null);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!subjectName.trim()) {
      toast.error("Subject name cannot be empty");
      return;
    }
    if (!selectedBoard) {
      toast.error("Please select a board");
      return;
    }
    setFormLoading(true);
    if (formMode === "add") {
      axios
        .post(`${baseURL}/subjects`, { name: subjectName, board: selectedBoard })
        .then((res) => {
          toast.success(res.data?.message || "Subject added successfully");
          setRefresh((p) => !p);
          handleCloseForm();
        })
        .catch((err) =>
          toast.error(
            err.response?.data?.error || err.response?.data?.message || "Failed to add subject"
          )
        )
        .finally(() => setFormLoading(false));
    } else {
      axios
        .put(`${baseURL}/subjects/${editId}`, { name: subjectName, board: selectedBoard })
        .then((res) => {
          toast.success(res.data?.message || "Subject updated successfully");
          setRefresh((p) => !p);
          handleCloseForm();
        })
        .catch((err) =>
          toast.error(
            err.response?.data?.error || err.response?.data?.message || "Failed to update subject"
          )
        )
        .finally(() => setFormLoading(false));
    }
  };

  const handleConfirmDelete = () => {
    // Soft delete: set isDeleted to true
    axios
      .put(`${baseURL}/subjects/${deleteId}`, { isDeleted: true })
      .then((res) => {
        toast.success(res.data?.message || "Subject deleted successfully");
        setRefresh((prev) => !prev);
        handleCloseDialog();
      })
      .catch((err) => {
        toast.error(err.response?.data?.error || "Failed to delete subject");
      });
  };

  const { columns, rows } = SubjectTableData(handleDeleteClick, handleEditClick, refresh);

  return (
    <ArgonBox>
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" px={4} mb={3}>
        <ArgonTypography variant="h6" size="small">
          All Subject
        </ArgonTypography>
        <ArgonButton variant="contained" color="primary" size="small" onClick={handleAddClick}>
          Add Subject
        </ArgonButton>
      </ArgonBox>
      <ArgonBox
        sx={{
          "& .MuiTableRow-root:not(:last-child)": {
            "& td": {
              borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                `${borderWidth[1]} solid ${borderColor}`,
            },
          },
        }}
      >
        {rows.length > 0 ? (
          <Table columns={columns} rows={rows} />
        ) : (
          <ArgonBox textAlign="center" py={4}>
            <ArgonTypography variant="h6" color="text" fontWeight="medium">
              No subject found
            </ArgonTypography>
            <ArgonTypography variant="caption" color="secondary">
              Click &apos;Add Subject&apos; to create a new subject.
            </ArgonTypography>
          </ArgonBox>
        )}
      </ArgonBox>

      <DeleteDialog
        open={deleteDialog}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Subject"
        description="Are you sure you want to delete this subject?"
        confirmText="Delete Subject"
        cancelText="Cancel"
      />

      {/* Add/Edit Subject Dialog */}
      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
            <ArgonTypography variant="h5">
              {formMode === "add" ? "Add Subject" : "Edit Subject"}
            </ArgonTypography>
            <IconButton onClick={handleCloseForm} size="small">
              <Close />
            </IconButton>
          </ArgonBox>
        </DialogTitle>
        <Divider />
        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            {/* Board Dropdown */}
            <ArgonBox mb={2}>
              <ArgonTypography variant="caption" fontWeight="medium" mb={1}>
                Select Board
              </ArgonTypography>
              <FormControl fullWidth disabled={formLoading}>
                <Select
                  value={selectedBoard}
                  onChange={(e) => setSelectedBoard(e.target.value)}
                  displayEmpty
                  sx={{
                    height: "45px",
                    border: "1px solid #d2d6da",
                    borderRadius: "8px",
                  }}
                >
                  <MenuItem value="">
                    <em>Select a board</em>
                  </MenuItem>
                  {boards && boards.length > 0 ? (
                    boards.map((board) => (
                      <MenuItem key={board._id} value={board._id}>
                        {board.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <em>No boards available</em>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </ArgonBox>

            {/* Subject Name */}
            <ArgonBox mb={2}>
              <ArgonTypography variant="caption" fontWeight="medium" mb={1}>
                Subject Name
              </ArgonTypography>
              <ArgonBox
                component="input"
                type="text"
                value={subjectName}
                onChange={(e) => setSubjectName(e.target.value)}
                placeholder="Enter Subject Name"
                disabled={formLoading}
                sx={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d2d6da",
                  borderRadius: "8px",
                }}
              />
            </ArgonBox>
          </DialogContent>
          <DialogActions>
            <ArgonButton
              onClick={handleCloseForm}
              variant="outlined"
              color="secondary"
              disabled={formLoading}
            >
              Cancel
            </ArgonButton>
            <ArgonButton type="submit" variant="contained" color="primary" disabled={formLoading}>
              {formMode === "add"
                ? formLoading
                  ? "Adding..."
                  : "Add Subject"
                : formLoading
                ? "Updating..."
                : "Update Subject"}
            </ArgonButton>
          </DialogActions>
        </form>
      </Dialog>
    </ArgonBox>
  );
}

export default index;
